<script setup>
import { useStore } from 'vuex';
import { ref, computed, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { addNode } from '../utils/flowHelpers';
import { getNodeCatalog } from '../composables/nodeCatalog';

const CONNECTION_GUIDANCE_DURATION = 4000;

const store = useStore();
const { t } = useI18n();
const showConnectionGuidance = ref(false);
let guidanceTimer = null;

onUnmounted(() => {
  if (guidanceTimer) clearTimeout(guidanceTimer);
});

// Start with only essential categories expanded for cleaner UX.
// "pipeline" incluído por ser o caso de uso mais comum em automações de CRM
// (mover/atualizar cards). "advanced" fica collapsed propositalmente — leigo
// vê a categoria mas é sinalizada como avançada (HTTP, webhook, captain AI).
const expandedCategories = ref(
  new Set(['triggers', 'conditions', 'messaging', 'pipeline'])
);
const searchQuery = ref('');
const nodeCategories = computed(() => getNodeCatalog(t));

const filteredCategories = computed(() => {
  const query = searchQuery.value.toLowerCase().trim();
  if (!query) return nodeCategories.value;

  return nodeCategories.value
    .map(category => ({
      ...category,
      items: category.items.filter(item =>
        item.label.toLowerCase().includes(query)
      ),
    }))
    .filter(category => category.items.length > 0);
});

// Tooltip de exemplo concreto para leigos entenderem o uso real de cada node.
// Reaproveita PIPELINE.FLOW_BUILDER.NODE.TRIGGER.DESCRIPTION.{subtype} já
// traduzido; para actions sem descrição, mostra só o label.
const getItemExample = item => {
  const subtype = item.data?.type || item.data?.event_type;
  if (!subtype) return item.label;
  if (item.type === 'trigger') {
    const key = `PIPELINE.FLOW_BUILDER.NODE.TRIGGER.DESCRIPTION.${subtype}`;
    const translated = t(key);
    if (translated !== key) return `${item.label} — ${translated}`;
  }
  return item.label;
};

const toggleCategory = key => {
  if (expandedCategories.value.has(key)) {
    expandedCategories.value.delete(key);
    // Trigger reactivity for Set mutation
    expandedCategories.value = new Set(expandedCategories.value);
  } else {
    expandedCategories.value.add(key);
    expandedCategories.value = new Set(expandedCategories.value);
  }
};

const handleDragStart = (event, item) => {
  event.dataTransfer.setData(
    'application/flow-builder-node',
    JSON.stringify({
      type: item.type,
      data: { ...item.data, label: item.label },
    })
  );
  event.dataTransfer.effectAllowed = 'copy';
};

const addNodeToFlow = item => {
  addNode(store, {
    type: item.type,
    data: { ...item.data, label: item.label },
  });

  // Annotation nodes don't need connection guidance
  if (item.type === 'annotation') return;

  // Show connection guidance tooltip briefly
  clearTimeout(guidanceTimer);
  showConnectionGuidance.value = true;
  guidanceTimer = setTimeout(() => {
    showConnectionGuidance.value = false;
  }, CONNECTION_GUIDANCE_DURATION);
};

const canUndo = computed(() => store.getters['automationFlow/canUndo']);
const canRedo = computed(() => store.getters['automationFlow/canRedo']);

const undo = () => {
  store.commit('automationFlow/undo');
};

const redo = () => {
  store.commit('automationFlow/redo');
};
</script>

<template>
  <div
    class="flex flex-col w-[280px] overflow-y-auto border-l border-n-weak bg-n-solid-2"
  >
    <!-- Header -->
    <div class="p-3 flex items-center justify-between border-b border-n-weak">
      <h3 class="text-heading-3 text-n-slate-12">
        {{ t('PIPELINE.FLOW_BUILDER.TOOLBAR.TITLE') }}
      </h3>

      <!-- History controls -->
      <div class="flex gap-1">
        <button
          class="p-1.5 rounded-md hover:bg-n-alpha-1 text-n-slate-10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
          :aria-label="t('PIPELINE.FLOW_BUILDER.TOOLBAR.UNDO')"
          :title="t('PIPELINE.FLOW_BUILDER.TOOLBAR.UNDO')"
          :disabled="!canUndo"
          @click="undo"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M3 10h10a6 6 0 0 1 0 12H8" />
            <path d="M7 10L3 6l4-4" />
          </svg>
        </button>
        <button
          class="p-1.5 rounded-md hover:bg-n-alpha-1 text-n-slate-10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
          :aria-label="t('PIPELINE.FLOW_BUILDER.TOOLBAR.REDO')"
          :title="t('PIPELINE.FLOW_BUILDER.TOOLBAR.REDO')"
          :disabled="!canRedo"
          @click="redo"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M21 10h-10a6 6 0 0 0 0 12h5" />
            <path d="M17 10l4-4-4-4" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Connection guidance tooltip -->
    <Transition
      enter-active-class="transition-opacity duration-300"
      leave-active-class="transition-opacity duration-300"
      enter-from-class="opacity-0"
      leave-to-class="opacity-0"
    >
      <div
        v-if="showConnectionGuidance"
        class="mx-3 mt-2 flex items-start gap-2 rounded-md bg-n-brand/10 px-3 py-2 text-xs text-n-brand"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="flex-shrink-0 mt-px"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
        <span>{{
          t('PIPELINE.FLOW_BUILDER.TOOLBAR.CONNECTION_GUIDANCE')
        }}</span>
      </div>
    </Transition>

    <!-- Search -->
    <div class="relative px-3 py-2 border-b border-n-weak">
      <span
        class="absolute left-5 top-1/2 -translate-y-1/2 i-lucide-search size-3.5 text-n-slate-10 pointer-events-none"
        aria-hidden="true"
      />
      <input
        v-model="searchQuery"
        type="text"
        :aria-label="t('PIPELINE.FLOW_BUILDER.TOOLBAR.SEARCH_PLACEHOLDER')"
        class="w-full pl-7 pr-2.5 py-1.5 text-sm rounded-md border border-n-weak bg-n-background text-n-slate-12 placeholder-n-slate-10 focus:outline-none focus:ring-1 focus:ring-n-brand"
        :placeholder="t('PIPELINE.FLOW_BUILDER.TOOLBAR.SEARCH_PLACEHOLDER')"
      />
    </div>

    <!-- Node categories -->
    <div class="flex-1 overflow-y-auto pb-3 pt-2">
      <div
        v-for="category in filteredCategories"
        :key="category.key"
        class="mx-3 mb-3 rounded-md overflow-hidden bg-n-solid-2 outline outline-1 outline-n-weak"
      >
        <button
          class="flex items-center w-full p-2.5 cursor-pointer hover:bg-n-alpha-1 text-left"
          :class="{
            'bg-n-alpha-1': expandedCategories.has(category.key),
          }"
          :aria-expanded="expandedCategories.has(category.key)"
          @click="toggleCategory(category.key)"
          @keydown.enter="toggleCategory(category.key)"
        >
          <div
            class="flex items-center justify-center w-6 h-6 rounded-md mr-2"
            :class="{
              'bg-blue-3 text-blue-11': category.icon === 'trigger',
              'bg-amber-3 text-amber-11': category.icon === 'condition',
              'bg-teal-3 text-teal-11': category.icon === 'action',
              'bg-yellow-3 text-yellow-11': category.icon === 'utility',
            }"
          >
            <!-- Utility (sticky-note) icon -->
            <svg
              v-if="category.icon === 'utility'"
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path
                d="M15.5 3H5a2 2 0 0 0-2 2v14c0 1.1.9 2 2 2h14a2 2 0 0 0 2-2V8.5L15.5 3Z"
              />
              <path d="M15 3v6h6" />
            </svg>

            <!-- Trigger icon -->
            <svg
              v-else-if="category.icon === 'trigger'"
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
            </svg>

            <!-- Condition icon -->
            <svg
              v-else-if="category.icon === 'condition'"
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M6 9l6 6 6-6" />
            </svg>

            <!-- Action icon -->
            <svg
              v-else
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
            </svg>
          </div>

          <span class="text-label-small font-medium text-n-slate-12 flex-grow">
            {{ category.label }}
          </span>

          <!-- Marca categoria "Avançado" pra leigo saber que pode pular -->
          <span
            v-if="category.key === 'advanced'"
            class="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-n-alpha-2 text-n-slate-10 mr-1"
          >
            {{ t('PIPELINE.FLOW_BUILDER.TOOLBAR.ADVANCED_BADGE') }}
          </span>

          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="transition-transform text-n-slate-10"
            :class="{ 'rotate-180': expandedCategories.has(category.key) }"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>

        <div
          v-if="expandedCategories.has(category.key) || searchQuery"
          class="border-t border-n-weak divide-y divide-n-weak"
        >
          <button
            v-for="item in category.items"
            :key="`${item.type}-${item.data.type || item.data.field}`"
            class="flex items-center justify-between w-full p-2.5 cursor-grab hover:bg-n-alpha-1 text-left active:cursor-grabbing focus:outline-none focus:bg-n-alpha-1"
            draggable="true"
            :title="getItemExample(item)"
            @click="addNodeToFlow(item)"
            @keydown.enter="addNodeToFlow(item)"
            @dragstart="handleDragStart($event, item)"
          >
            <div class="flex items-center">
              <div
                class="w-2 h-2 rounded-full mr-2 flex-shrink-0"
                :class="{
                  'bg-blue-9': item.type === 'trigger',
                  'bg-amber-9': item.type === 'condition',
                  'bg-teal-9': item.type === 'action',
                  'bg-yellow-9': item.type === 'annotation',
                }"
              />
              <span class="text-label-small text-n-slate-11">{{
                item.label
              }}</span>
            </div>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="text-n-slate-10 flex-shrink-0"
            >
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </button>
        </div>
      </div>

      <div
        v-if="filteredCategories.length === 0 && searchQuery"
        class="flex flex-col items-center justify-center px-3 py-8 text-center"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="text-n-slate-8 mb-2"
        >
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <p class="text-label-small text-n-slate-10">
          {{ t('PIPELINE.FLOW_BUILDER.TOOLBAR_EMPTY') }}
        </p>
        <p class="text-[11px] text-n-slate-8 mt-1">
          {{ t('PIPELINE.FLOW_BUILDER.TOOLBAR_EMPTY_HINT') }}
        </p>
      </div>
    </div>
  </div>
</template>
